import { VOTE_COUNT_THRESHOLDS } from '../constants';

function getReleaseYear(item) {
  const d = item.release_date || item.first_air_date;
  if (!d) return null;
  const y = parseInt(String(d).slice(0, 4), 10);
  return Number.isFinite(y) ? y : null;
}

function filterByGenre(items, selectedGenre) {
  if (!selectedGenre || selectedGenre === 'all') return items;

  return items.filter((item) => {
    const ids = item.genre_ids || [];
    if (selectedGenre === 'anime') {
      const hasAnimation = ids.includes(16);
      const jp =
        item.original_language === 'ja' ||
        (Array.isArray(item.origin_country) && item.origin_country.includes('JP'));
      return hasAnimation && jp;
    }
    const n = Number(selectedGenre);
    if (!Number.isFinite(n)) return true;
    return ids.includes(n);
  });
}

function filterByYearRange(items, fromYear, toYear) {
  if (!fromYear && !toYear) return items;
  const from = fromYear ? parseInt(fromYear, 10) : null;
  const to = toYear ? parseInt(toYear, 10) : null;

  return items.filter((item) => {
    const y = getReleaseYear(item);
    if (y == null) return false;
    if (from != null && y < from) return false;
    if (to != null && y > to) return false;
    return true;
  });
}

function filterByRatings(items, imdbRating, rottenTomatoesRating) {
  if (imdbRating === 0 && rottenTomatoesRating === 0) return items;

  return items.filter((item) => {
    const enhancedImdb = item.enhanced_ratings?.real_ratings?.imdb?.rating || 0;
    const enhancedRt = item.enhanced_ratings?.real_ratings?.rottenTomatoes?.rating || 0;
    const enhancedMeta = item.enhanced_ratings?.real_ratings?.metacritic?.rating || 0;

    const ratingsArray = item.ratings || [];
    const mdblistImdb = ratingsArray.find((r) => r.source === 'imdb')?.value || 0;
    const mdblistRt = ratingsArray.find((r) => r.source === 'tomatoes')?.value || 0;
    const mdblistMeta = ratingsArray.find((r) => r.source === 'metacritic')?.value || 0;

    const mdblistScore = item.score_average || item.score || 0;

    const imdbR = enhancedImdb || mdblistImdb / 10 || 0;
    const rtR = enhancedRt || mdblistRt || 0;
    const metacriticR = enhancedMeta || mdblistMeta || 0;

    const hasImdb = imdbR > 0;
    const hasRt = rtR > 0;
    const hasMetacritic = metacriticR > 0;
    const hasMdblist = mdblistScore > 0;

    if (!hasImdb && !hasRt && !hasMetacritic && !hasMdblist) {
      return false;
    }

    let passesImdb = true;
    let passesRt = true;

    if (imdbRating > 0) {
      if (hasImdb) {
        passesImdb = imdbR >= imdbRating;
      } else if (hasRt) {
        passesImdb = rtR / 10 >= imdbRating;
      } else if (hasMetacritic) {
        passesImdb = metacriticR / 10 >= imdbRating;
      } else if (hasMdblist) {
        passesImdb = mdblistScore / 10 >= imdbRating;
      } else {
        passesImdb = false;
      }
    }

    if (rottenTomatoesRating > 0) {
      if (hasRt) {
        passesRt = rtR >= rottenTomatoesRating;
      } else if (hasImdb) {
        passesRt = imdbR * 10 >= rottenTomatoesRating;
      } else if (hasMetacritic) {
        passesRt = metacriticR >= rottenTomatoesRating;
      } else if (hasMdblist) {
        passesRt = mdblistScore >= rottenTomatoesRating;
      } else {
        passesRt = false;
      }
    }

    return passesImdb && passesRt;
  });
}

/**
 * LT availability is applied server-side on browse endpoints; search/multi payloads
 * do not consistently include per-item LT streaming flags, so we do not filter here.
 */
function filterByAvailableLT(items, availableLT) {
  if (availableLT !== 'true' && availableLT !== true) return items;
  return items.filter((item) => {
    if (item.available_in_lt === true) return true;
    if (item.available_in_lt === false) return false;
    if (item.has_lt_streaming === true) return true;
    return true;
  });
}

function ratingComparator(a, b) {
  const aImdb = a?.enhanced_ratings?.real_ratings?.imdb?.rating ?? 0;
  const bImdb = b?.enhanced_ratings?.real_ratings?.imdb?.rating ?? 0;
  const aRt = a?.enhanced_ratings?.real_ratings?.rottenTomatoes?.rating ?? 0;
  const bRt = b?.enhanced_ratings?.real_ratings?.rottenTomatoes?.rating ?? 0;

  const aHasReal = aImdb > 0 || aRt > 0;
  const bHasReal = bImdb > 0 || bRt > 0;

  const aVoteCount = a?.vote_count ?? 0;
  const bVoteCount = b?.vote_count ?? 0;

  const aPriority = aHasReal ? 0 : aVoteCount >= VOTE_COUNT_THRESHOLDS.HIGH_QUALITY_THRESHOLD ? 1 : 2;
  const bPriority = bHasReal ? 0 : bVoteCount >= VOTE_COUNT_THRESHOLDS.HIGH_QUALITY_THRESHOLD ? 1 : 2;

  if (aPriority !== bPriority) return aPriority - bPriority;

  let aScore;
  let bScore;

  if (aPriority === 0) {
    aScore = aImdb || aRt / 10 || (a.vote_average ?? 0);
    bScore = bImdb || bRt / 10 || (b.vote_average ?? 0);
  } else {
    aScore = a.vote_average ?? 0;
    bScore = b.vote_average ?? 0;
  }

  return bScore - aScore;
}

function sortSearchResults(items, sortBy) {
  if (!Array.isArray(items)) return items;

  switch (sortBy) {
    case 'vote_average':
      return [...items].sort(ratingComparator);
    case 'revenue':
      return [...items].sort((a, b) => (b.revenue || 0) - (a.revenue || 0));
    case 'popularity':
      return [...items].sort((a, b) => (b.popularity || 0) - (a.popularity || 0));
    case 'trending':
    default:
      return items;
  }
}

/**
 * Client-side filters for /search (TMDB multi) so the same controls as the home page apply.
 */
export function applySearchResultFilters(items, filters) {
  if (!Array.isArray(items)) return [];

  const selectedGenre = filters.selectedGenre || 'all';
  const sortBy = filters.sortBy || 'trending';
  const fromYear = filters.fromYear || '';
  const toYear = filters.toYear || '';
  const imdbRating = filters.imdbRating || 0;
  const rottenTomatoesRating = filters.rottenTomatoesRating || 0;
  const availableLT = filters.availableLT || 'false';

  let out = [...items];
  out = filterByGenre(out, selectedGenre);
  out = filterByYearRange(out, fromYear, toYear);
  out = filterByRatings(out, imdbRating, rottenTomatoesRating);
  out = filterByAvailableLT(out, availableLT);
  out = sortSearchResults(out, sortBy);
  return out;
}
